import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { AuthGateway, SessionInfo } from "./gateway";
import type { TenantMembershipDto } from "../../types";
import { fetchMyTenants, switchTenant as apiSwitchTenant } from "../api/endpoints";
import { describeAuthError } from "../authErrors";

export type SessionStatus = "loading" | "anon" | "authed";

export interface SessionContextValue {
  status: SessionStatus;
  userEmail: string | null;
  accessToken: string | null;
  tenants: TenantMembershipDto[];
  tenantsLoaded: boolean;
  activeTenantId: string | null;
  activeTenant: TenantMembershipDto | null;
  ctLimit: number;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  switchTenant: (tenantId: string) => Promise<void>;
  refreshTenants: (tenantId?: string) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updatePassword: (password: string) => Promise<void>;
  clearError: () => void;
}

const SessionContext = createContext<SessionContextValue | null>(null);

interface SessionProviderProps {
  gateway: AuthGateway;
  children: ReactNode;
}

export function SessionProvider({ gateway, children }: SessionProviderProps) {
  const [status, setStatus] = useState<SessionStatus>("loading");
  const [session, setSession] = useState<SessionInfo | null>(null);
  const [tenants, setTenants] = useState<TenantMembershipDto[]>([]);
  const [tenantsLoaded, setTenantsLoaded] = useState(false);
  const [ctLimit, setCtLimit] = useState(1);
  const [activeTenantId, setActiveTenantId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadTenants = useCallback(async (token: string) => {
    const res = await fetchMyTenants(token);
    setTenants(res.tenants);
    setCtLimit(res.ctLimit);
    return res;
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const existing = await gateway.getSession();
        if (cancelled) return;
        if (existing) {
          setSession(existing);
          setStatus("authed");
          try {
            const res = await loadTenants(existing.accessToken);
            if (!cancelled) {
              setTenants(res.tenants);
              if (res.tenants.length === 1) setActiveTenantId(res.tenants[0]!.tenantId);
            }
          } catch (e) {
            if (!cancelled) setError(describeAuthError(e));
          } finally {
            if (!cancelled) setTenantsLoaded(true);
          }
        } else {
          setStatus("anon");
          setTenantsLoaded(true);
        }
      } catch (e) {
        if (!cancelled) {
          setError(describeAuthError(e));
          setStatus("anon");
          setTenantsLoaded(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [gateway, loadTenants]);

  const login = useCallback(
    async (email: string, password: string) => {
      setError(null);
      try {
        const s = await gateway.signIn(email, password);
        setSession(s);
        setStatus("authed");
        const res = await loadTenants(s.accessToken);
        setTenants(res.tenants);
        setTenantsLoaded(true);
        if (res.tenants.length === 1) setActiveTenantId(res.tenants[0]!.tenantId);
      } catch (e) {
        setError(describeAuthError(e));
        setSession(null);
        setStatus("anon");
      }
    },
    [gateway, loadTenants],
  );

  const signup = useCallback(
    async (email: string, password: string) => {
      setError(null);
      try {
        const s = await gateway.signUp(email, password);
        setSession(s);
        setStatus("authed");
        const res = await loadTenants(s.accessToken);
        setTenants(res.tenants);
        setTenantsLoaded(true);
      } catch (e) {
        setError(describeAuthError(e));
        setSession(null);
        setStatus("anon");
      }
    },
    [gateway, loadTenants],
  );

  const logout = useCallback(async () => {
    await gateway.signOut();
    setSession(null);
    setTenants([]);
    setTenantsLoaded(false);
    setActiveTenantId(null);
    setError(null);
    setStatus("anon");
  }, [gateway]);

  const switchTenant = useCallback(
    async (tenantId: string) => {
      setError(null);
      try {
        const token = gateway.getAccessToken() ?? session?.accessToken;
        if (!token) return;
        await apiSwitchTenant(token, tenantId);
        const refreshed = await gateway.refreshSession();
        setSession(refreshed);
        const res = await loadTenants(refreshed.accessToken);
        setTenants(res.tenants);
        setActiveTenantId(tenantId);
      } catch (e) {
        setError(describeAuthError(e));
      }
    },
    [gateway, session, loadTenants],
  );

  const refreshTenants = useCallback(
    async (tenantId?: string) => {
      setError(null);
      try {
        const token = gateway.getAccessToken() ?? session?.accessToken;
        if (!token) return;
        // Após aceitar um convite o backend troca o tenant ativo no Supabase;
        // o refresh do JWT propaga o claim e recarrega as memberships.
        const refreshed = await gateway.refreshSession();
        setSession(refreshed);
        const res = await loadTenants(refreshed.accessToken);
        setTenants(res.tenants);
        setTenantsLoaded(true);
        if (tenantId) setActiveTenantId(tenantId);
      } catch (e) {
        setError(describeAuthError(e));
      }
    },
    [gateway, session, loadTenants],
  );

  const resetPassword = useCallback(
    async (email: string) => {
      setError(null);
      try {
        const redirectTo = `${window.location.origin}/reset-password`;
        await gateway.resetPassword(email, redirectTo);
      } catch (e) {
        const message = describeAuthError(e);
        setError(message);
        throw new Error(message);
      }
    },
    [gateway],
  );

  const updatePassword = useCallback(
    async (password: string) => {
      setError(null);
      try {
        await gateway.updatePassword(password);
      } catch (e) {
        const message = describeAuthError(e);
        setError(message);
        throw new Error(message);
      }
    },
    [gateway],
  );

  const clearError = useCallback(() => setError(null), []);

  const value = useMemo<SessionContextValue>(
    () => ({
      status,
      userEmail: session?.email ?? null,
      accessToken: session?.accessToken ?? null,
      tenants,
      tenantsLoaded,
      activeTenantId,
      activeTenant: tenants.find(t => t.tenantId === activeTenantId) ?? null,
      ctLimit,
      error,
      login,
      signup,
      logout,
      switchTenant,
      refreshTenants,
      resetPassword,
      updatePassword,
      clearError,
    }),
    [
      status,
      session,
      tenants,
      tenantsLoaded,
      activeTenantId,
      ctLimit,
      error,
      login,
      signup,
      logout,
      switchTenant,
      refreshTenants,
      resetPassword,
      updatePassword,
      clearError,
    ],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSession deve ser usado dentro de SessionProvider");
  return ctx;
}
